export const csharpLessons = [
  {
    slug: 'singleton',
    title: 'Singleton',
    icon: '⬛',
    content: `
# Singleton Pattern

The Singleton pattern ensures a class has **only one instance** and provides a global access point to it.

## When to Use

- Configuration managers
- Connection pools
- Logging services
- Cache managers

## Basic Thread-Safe Implementation

\`\`\`csharp
public class Singleton
{
    private static Singleton _instance;
    private static readonly object _lock = new object();

    private Singleton() { }

    public static Singleton Instance
    {
        get
        {
            lock (_lock)
            {
                if (_instance == null)
                    _instance = new Singleton();
                return _instance;
            }
        }
    }
}
\`\`\`

## Modern: Lazy Initialization

\`\`\`csharp
public class Singleton
{
    private static readonly Lazy<Singleton> _instance =
        new Lazy<Singleton>(() => new Singleton());

    private Singleton() { }

    public static Singleton Instance => _instance.Value;

    public void DoWork() => Console.WriteLine("Singleton working...");
}

// Usage
Singleton.Instance.DoWork();
\`\`\`

## Preferred: .NET DI Container

In modern .NET apps, register as singleton in the DI container instead of implementing it manually:

\`\`\`csharp
// Program.cs
builder.Services.AddSingleton<IMyService, MyService>();
\`\`\`

## Key Takeaways

- Use \`Lazy<T>\` for thread-safe lazy initialization
- The \`private\` constructor prevents external instantiation
- In .NET, prefer DI container singletons over manual implementation
- Singletons make unit testing harder — inject interfaces, not concrete classes
`
  },
  {
    slug: 'factory',
    title: 'Factory',
    icon: '🏭',
    content: `
# Factory Pattern

The Factory pattern creates objects without exposing the creation logic to the caller. The caller works with an interface, not a concrete type.

## Problem Without Factory

\`\`\`csharp
// Tightly coupled — switching logger requires changing every call site
var logger = new FileLogger();
var logger = new ConsoleLogger();
\`\`\`

## Simple Factory

\`\`\`csharp
public interface ILogger
{
    void Log(string message);
}

public class FileLogger : ILogger
{
    public void Log(string message) => File.AppendAllText("log.txt", message);
}

public class ConsoleLogger : ILogger
{
    public void Log(string message) => Console.WriteLine(message);
}

public class DatabaseLogger : ILogger
{
    public void Log(string message) { /* write to DB */ }
}

public static class LoggerFactory
{
    public static ILogger Create(string type) => type switch
    {
        "file"     => new FileLogger(),
        "console"  => new ConsoleLogger(),
        "database" => new DatabaseLogger(),
        _ => throw new ArgumentException($"Unknown logger type: {type}")
    };
}

// Usage
ILogger logger = LoggerFactory.Create("file");
logger.Log("Application started");
\`\`\`

## Factory Method Pattern

Let subclasses decide which class to instantiate.

\`\`\`csharp
public abstract class Notification
{
    public abstract string Send(string message);
}

public class EmailNotification : Notification
{
    public override string Send(string message) => $"Email: {message}";
}

public class SmsNotification : Notification
{
    public override string Send(string message) => $"SMS: {message}";
}

public abstract class NotificationSender
{
    protected abstract Notification CreateNotification();

    public string Notify(string message)
    {
        var notification = CreateNotification();
        return notification.Send(message);
    }
}

public class EmailSender : NotificationSender
{
    protected override Notification CreateNotification() => new EmailNotification();
}

public class SmsSender : NotificationSender
{
    protected override Notification CreateNotification() => new SmsNotification();
}
\`\`\`

## Key Takeaways

- Factory decouples object creation from usage
- Program to an interface (\`ILogger\`), not an implementation (\`FileLogger\`)
- Use Factory Method when subclasses should control instantiation
- In .NET, \`IServiceProvider\` often replaces manual factories
`
  },
  {
    slug: 'repository',
    title: 'Repository',
    icon: '🗃️',
    content: `
# Repository Pattern

The Repository pattern abstracts the data access layer, giving the rest of the app a clean interface to work with data — without knowing the underlying storage mechanism.

## Why Use It?

- Swap databases without changing business logic
- Easy to mock in unit tests
- Centralizes query logic in one place

## Interface Definition

\`\`\`csharp
public interface IUserRepository
{
    Task<User> GetByIdAsync(int id);
    Task<IEnumerable<User>> GetAllAsync();
    Task<User> AddAsync(User user);
    Task UpdateAsync(User user);
    Task DeleteAsync(int id);
}
\`\`\`

## Entity Framework Implementation

\`\`\`csharp
public class UserRepository : IUserRepository
{
    private readonly AppDbContext _context;

    public UserRepository(AppDbContext context)
    {
        _context = context;
    }

    public async Task<User> GetByIdAsync(int id)
        => await _context.Users.FindAsync(id);

    public async Task<IEnumerable<User>> GetAllAsync()
        => await _context.Users.ToListAsync();

    public async Task<User> AddAsync(User user)
    {
        _context.Users.Add(user);
        await _context.SaveChangesAsync();
        return user;
    }

    public async Task UpdateAsync(User user)
    {
        _context.Users.Update(user);
        await _context.SaveChangesAsync();
    }

    public async Task DeleteAsync(int id)
    {
        var user = await _context.Users.FindAsync(id);
        if (user != null)
        {
            _context.Users.Remove(user);
            await _context.SaveChangesAsync();
        }
    }
}
\`\`\`

## Generic Repository

\`\`\`csharp
public interface IRepository<T> where T : class
{
    Task<T> GetByIdAsync(int id);
    Task<IEnumerable<T>> GetAllAsync();
    Task AddAsync(T entity);
    Task UpdateAsync(T entity);
    Task DeleteAsync(int id);
}

public class Repository<T> : IRepository<T> where T : class
{
    protected readonly AppDbContext _context;
    protected readonly DbSet<T> _dbSet;

    public Repository(AppDbContext context)
    {
        _context = context;
        _dbSet = context.Set<T>();
    }

    public async Task<T> GetByIdAsync(int id) => await _dbSet.FindAsync(id);
    public async Task<IEnumerable<T>> GetAllAsync() => await _dbSet.ToListAsync();

    public async Task AddAsync(T entity)
    {
        await _dbSet.AddAsync(entity);
        await _context.SaveChangesAsync();
    }

    public async Task UpdateAsync(T entity)
    {
        _dbSet.Update(entity);
        await _context.SaveChangesAsync();
    }

    public async Task DeleteAsync(int id)
    {
        var entity = await _dbSet.FindAsync(id);
        if (entity != null)
        {
            _dbSet.Remove(entity);
            await _context.SaveChangesAsync();
        }
    }
}
\`\`\`

## Dependency Injection Registration

\`\`\`csharp
// Program.cs
builder.Services.AddScoped<IUserRepository, UserRepository>();
builder.Services.AddScoped(typeof(IRepository<>), typeof(Repository<>));
\`\`\`

## Key Takeaways

- Repository hides EF Core / SQL / any DB behind an interface
- Use \`IRepository<T>\` for simple CRUD; extend with domain-specific query methods
- Pair with Unit of Work pattern for multi-repository transactions
- Always inject \`IUserRepository\`, never the concrete \`UserRepository\`
`
  },
  {
    slug: 'dependency-injection',
    title: 'Dependency Injection',
    icon: '💉',
    content: `
# Dependency Injection

Dependency Injection (DI) is a technique where objects receive their dependencies from an external source rather than creating them internally.

## The Problem DI Solves

\`\`\`csharp
// Tightly coupled — impossible to swap or test in isolation
public class OrderService
{
    private readonly EmailService _emailService = new EmailService();
    private readonly SqlOrderRepo _repo = new SqlOrderRepo();
}
\`\`\`

## Constructor Injection

\`\`\`csharp
public interface IEmailService
{
    void Send(string to, string body);
}

public interface IOrderRepository
{
    Task SaveAsync(Order order);
}

public class OrderService
{
    private readonly IEmailService _emailService;
    private readonly IOrderRepository _orderRepository;

    public OrderService(IEmailService emailService, IOrderRepository orderRepository)
    {
        _emailService = emailService;
        _orderRepository = orderRepository;
    }

    public async Task PlaceOrder(Order order)
    {
        await _orderRepository.SaveAsync(order);
        _emailService.Send(order.CustomerEmail, "Order confirmed!");
    }
}
\`\`\`

## Registering with .NET DI Container

\`\`\`csharp
// Program.cs (.NET 6+)
var builder = WebApplication.CreateBuilder(args);

builder.Services.AddScoped<IOrderRepository, SqlOrderRepository>();
builder.Services.AddTransient<IEmailService, SmtpEmailService>();
builder.Services.AddScoped<OrderService>();

var app = builder.Build();
\`\`\`

## Service Lifetimes

| Lifetime | Created | Destroyed | Use For |
|----------|---------|-----------|---------|
| **Singleton** | Once per app | App shutdown | Config, caches |
| **Scoped** | Once per request | End of request | DB contexts |
| **Transient** | Each injection | After use | Lightweight services |

## Testing with DI

\`\`\`csharp
// No real database or email needed
var mockRepo = new Mock<IOrderRepository>();
var mockEmail = new Mock<IEmailService>();

mockRepo.Setup(r => r.SaveAsync(It.IsAny<Order>())).Returns(Task.CompletedTask);

var orderService = new OrderService(mockEmail.Object, mockRepo.Object);

await orderService.PlaceOrder(new Order { CustomerEmail = "test@test.com" });

mockEmail.Verify(e => e.Send("test@test.com", It.IsAny<string>()), Times.Once);
\`\`\`

## Key Takeaways

- **Constructor injection** is the preferred DI method in .NET
- Program to **interfaces**, not concrete implementations
- **Scoped** is the right lifetime for most services (e.g., DbContext)
- DI makes code testable, maintainable, and decoupled
- .NET's built-in DI container handles most use cases without third-party libs
`
  }
];
